'use client';

import React from 'react';
import { CheckCircle2, Circle, FileText } from 'lucide-react';
import { DayContent, DAY_LABELS } from '@/lib/curriculum';

interface DayPanelProps {
  weekNum: number;
  dayIndex: number;
  dayContent: DayContent;
  isDayComplete: boolean;
  note: string;
  onToggleDay: () => void;
  onNoteChange: (text: string) => void;
}

export default function DayPanel({
  weekNum,
  dayIndex,
  dayContent,
  isDayComplete,
  note,
  onToggleDay,
  onNoteChange,
}: DayPanelProps) {
  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        border: `1px solid ${isDayComplete ? 'var(--accent-border)' : 'var(--border)'}`,
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'border-color 150ms ease',
      }}
    >
      {/* Day header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: isDayComplete ? 'var(--accent-glow)' : 'var(--bg-elevated)',
        }}
      >
        <div>
          <div
            style={{
              fontSize: '11px',
              fontFamily: 'IBM Plex Mono, monospace',
              fontWeight: 600,
              color: 'var(--text-muted)',
              letterSpacing: '0.06em',
              marginBottom: '4px',
            }}
          >
            {DAY_LABELS[dayIndex].toUpperCase()}
          </div>
          <h2
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              fontSize: '20px',
              fontWeight: 400,
              color: 'var(--text-primary)',
              lineHeight: 1.2,
            }}
          >
            {dayContent.topic}
          </h2>
        </div>
        <button
          onClick={onToggleDay}
          aria-label={isDayComplete ? 'Mark day incomplete' : 'Mark day complete'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            borderRadius: '8px',
            border: `1px solid ${isDayComplete ? 'var(--accent-border)' : 'var(--border)'}`,
            backgroundColor: isDayComplete ? 'var(--accent)' : 'var(--bg-card)',
            color: isDayComplete ? '#fff' : 'var(--text-secondary)',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            transition: 'all 150ms ease',
          }}
        >
          {isDayComplete ? <CheckCircle2 size={15} /> : <Circle size={15} />}
          {isDayComplete ? 'Done' : 'Mark Done'}
        </button>
      </div>

      {/* Task */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
        <div
          style={{
            fontSize: '11px',
            fontFamily: 'IBM Plex Mono, monospace',
            fontWeight: 600,
            color: 'var(--text-muted)',
            letterSpacing: '0.06em',
            marginBottom: '8px',
          }}
        >
          TODAY'S TASK
        </div>
        <p
          style={{
            fontSize: '14px',
            color: 'var(--text-primary)',
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {dayContent.task}
        </p>
      </div>

      {/* Notes editor */}
      <div style={{ padding: '16px 20px' }}>
        <label
          htmlFor={`note-editor-w${weekNum}-d${dayIndex}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '11px',
            fontFamily: 'IBM Plex Mono, monospace',
            fontWeight: 600,
            color: 'var(--text-muted)',
            letterSpacing: '0.06em',
            marginBottom: '8px',
            cursor: 'default',
          }}
        >
          <FileText size={12} />
          NOTES — AUTO-SAVED
        </label>
        <textarea
          id={`note-editor-w${weekNum}-d${dayIndex}`}
          value={note}
          onChange={e => onNoteChange(e.target.value)}
          placeholder={`Write your notes for ${DAY_LABELS[dayIndex]}…\n\nCapture key learnings, code snippets, questions, or anything you want to remember.`}
          aria-label={`Notes for ${DAY_LABELS[dayIndex]}`}
          style={{
            width: '100%',
            minHeight: '160px',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            backgroundColor: 'var(--bg-input)',
            color: 'var(--text-primary)',
            fontSize: '14px',
            lineHeight: 1.6,
            outline: 'none',
            transition: 'border-color 150ms ease',
            fontFamily: 'DM Sans, system-ui, sans-serif',
          }}
          onFocus={e => { (e.target as HTMLTextAreaElement).style.borderColor = 'var(--accent-border)'; }}
          onBlur={e => { (e.target as HTMLTextAreaElement).style.borderColor = 'var(--border)'; }}
        />
        <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px', fontFamily: 'IBM Plex Mono, monospace' }}>
          Notes are saved automatically to your browser
        </p>
      </div>
    </div>
  );
}